import { IconCheck, useLocalization } from '@magicdoor/ui';
import { useLocation, useNavigate } from '@solidjs/router';
import { createEffect, createMemo, createSignal, Show } from 'solid-js';
import { Button } from '~/components/common/Buttons';
import { useOnboarding } from '~/contexts/global';
import { isDemo } from '~/utils/constant';
import { getCurrentProgress } from './utils';

interface CompletionBarProps {
  title?: string;
  subtitle?: string;
  onRetract?: () => void;
}

export const CompletionBar = (props: CompletionBarProps) => {
  const { t } = useLocalization();
  const { companies, isStepCompleted, isOnboardingCompletedAndWithoutUnsentNotifications, hideOnboarding } = useOnboarding();
  const location = useLocation();
  const navigate = useNavigate();
  const [progress, setProgress] = createSignal(10);

  createEffect(() => {
    const currentProgress = getCurrentProgress(companies()?.onboardingStatus, (step) => isStepCompleted(step));
    setProgress(currentProgress);
  });

  return (
    <div class="relative flex flex-col rounded-lg border bg-white p-5">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-sm font-semibold capitalize md:text-base">{props.title || t('Complete steps to start using your account!')}</h3>
          <p class="mt-1 text-xs text-purple md:text-sm">{props.subtitle || t('Enable 50+ features by completing onboarding')}</p>
        </div>
        <div class="flex items-center gap-2">
          <Show when={!location.pathname.startsWith('/onboarding')}>
            <Button variant="outlined" rounded="full" size="sm" onClick={() => navigate('/onboarding')}>
              {t('Continue onboarding')}
            </Button>
          </Show>

          <Show when={isOnboardingCompletedAndWithoutUnsentNotifications()}>
            <Button
              rounded="full"
              size="sm"
              loading={hideOnboarding.loading}
              onClick={() => {
                hideOnboarding();
              }}>
              <IconCheck class="size-4" />
              {t("I'm done with onboarding")}
            </Button>
          </Show>
        </div>
      </div>

      <div class="mt-4 w-full rounded-full border border-essential-colour bg-white p-[2px]">
        <div class="relative h-4 rounded-full bg-gradient-to-r from-[#F1ACFF] to-[#7B41DF]" style={{ width: `${progress()}%` }}>
          <div class="absolute -right-4 top-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-[#7B41DF] p-1 px-3 text-center text-sm text-white">
            {progress()}%
          </div>
        </div>
      </div>
    </div>
  );
};

export const CompletionBarInNavigation = () => {
  const { isOnboardingCompleted, companies, companyStatus } = useOnboarding();
  const location = useLocation();

  const shouldShow = createMemo(() => {
    return companies() && companyStatus() && !location.pathname.startsWith('/onboarding') && !isOnboardingCompleted() && !isDemo();
  });

  return (
    <Show when={shouldShow()}>
      <CompletionBar />
    </Show>
  );
};
